import { useState, useEffect } from "react";
import Image from "next/image";
import { validate } from "../../../utils/form-functions";
import { InfoBox } from "../../components/form/InfoBox";
import { sendGaEvent, sendLinkedInConversion } from "../../../../config/userTracking";
import { userApi } from "../../../../api/userApi";
import { formAttribution } from "../../../../utils/formAttribution";
import clsx from "clsx";

import { RequestDemoForm } from "./components/RequestDemoForm";
import { useRouter } from "next/router";
import { logos } from "../../../../constants/CustomerLogos";

const renderCustomerLogo = logo => (
  <div
    key={logo.alt}
    className={clsx("logo", logo.isBrightLogo && "brightLogo")}
    style={logo.isAnz ? { marginTop: "0.5rem" } : {}}
  >
    <Image priority={false} src={logo.src} alt={logo.alt} width={logo.width} height={logo.height} />
  </div>
);

export const DiscoveryForm = ({ data, type, className }) => {
  const [modalOpen, changeModalOpen] = useState(false);
  const [errorModalOpen, changeErrorModalOpen] = useState(false);
  const [consent, changeConsent] = useState(false);
  const [formData, changeFormData] = useState({});
  const [errors, changeErrors] = useState({});
  const [sending, changeSending] = useState(false);
  const [linkedInCookie, setLinkedInCookie] = useState(null);

  useEffect(() => {
    setLinkedInCookie(localStorage.getItem("li_fat_id"));
  }, []);

  const changeData = (e, data) => {
    let newFormData = { ...formData };
    newFormData[data.id] = e;
    changeFormData(newFormData);
  };

  const preparedData = () => {
    let preparedData = formAttribution(
      type === "report"
        ? "Data Discovery Report Request"
        : type === "contact"
        ? "Contact Form"
        : "Demo Request Form",
    );
    data.fields.forEach(field => {
      preparedData[field.id] = formData[field.id];
    });
    return preparedData;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (validate({ type, formData, data, changeErrors, consent }) === true) {
      changeSending(true);
      const dataToSend = {
        type,
        formValues: preparedData(),
      };
      linkedInCookie && sendLinkedInConversion();

      if (type === "contact") {
        sendGaEvent(
          {
            action: "Form Submission",
            category: "Contact",
          },
          formData.email,
        );
      }
      if (type === "request") {
        sendGaEvent(
          {
            action: "Form Submission",
            category: "Request Demo",
          },
          formData.email,
        );
      }
      try {
        await userApi.sendFrontForm(dataToSend);
        changeModalOpen(true);
        changeSending(false);
      } catch (e) {
        changeErrorModalOpen(true);
        changeSending(false);
      }
    }
  };

  useEffect(() => {
    let newFormData = {};
    data.fields.forEach(field => {
      newFormData[field.id] = "";
    });
    changeFormData(newFormData);
  }, []);
  const mobileScreenWidth = 30 * 16;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isMobile = window.innerWidth <= mobileScreenWidth;
      const headerElement = document.querySelector("header");
      const footerElement = document.querySelector("footer");
      if (headerElement) {
        headerElement.remove();
      }
      if (footerElement) {
        footerElement.remove();
      }
      if (!isMobile) {
        document.body.style.overflowY = "hidden";

        return () => {
          document.body.style.overflowY = "auto";
        };
      }
    }
  }, []);

  return (
    <>
      {modalOpen && <InfoBox closeModal={() => changeModalOpen(false)} />}
      {errorModalOpen && (
        <InfoBox closeModal={() => changeErrorModalOpen(false)} type="Error" fromContact={true} />
      )}
      {["contact", "request"].includes(type) &&
        typeof formData[data.fields[0].id] !== "undefined" && (
          <RequestDemoForm
            data={data}
            formData={formData}
            changeData={changeData}
            errors={errors}
            consent={consent}
            className={className}
            changeConsent={changeConsent}
            handleSubmit={handleSubmit}
            sending={sending}
            type={type}
            classNameForTextAgreement="terms-agreement"
          />
        )}
    </>
  );
};
export const RequestDemoTemplate = ({ data, type }) => {
  const router = useRouter();

  return (
    <section className={clsx("LandingHero-background-request-demo")}>
      <div className="left-side">
        <DiscoveryForm data={data} type={type} />
      </div>
      <div className="right-side">
        <div className="AlignSvgRequestDemo">
          <button className={"x-button"} onClick={() => router.back()}>
            <svg
              width="22"
              height="21"
              viewBox="0 0 22 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.7789 10.5L21.4332 17.8064C22.1889 18.5277 22.1889 19.7377 21.4332 20.459C20.6776 21.1803 19.41 21.1803 18.6543 20.459L11 13.1526L3.34571 20.4357C2.59003 21.1571 1.32244 21.1571 0.566759 20.4357C-0.18892 19.7144 -0.18892 18.5044 0.566759 17.7831L8.22105 10.4767L0.566759 3.19363C-0.18892 2.4723 -0.18892 1.26233 0.566759 0.540997C1.32244 -0.180332 2.59003 -0.180332 3.34571 0.540997L11 7.84737L18.6299 0.540997C19.3856 -0.180332 20.6532 -0.180332 21.4089 0.540997C22.1645 1.26233 22.1645 2.4723 21.4089 3.19363L13.7789 10.5Z"
                fill="#F4F3FF"
              />
            </svg>
          </button>
        </div>
        <div className="text-right-side">
          <div className="text-center-wrapper">
            <div className="align-text-and-logos">
              <div className="text-right">
                <p>
                  For over a decade Demyst has been powering some of the most critical bank and
                  insurance applications around the world.
                </p>
                <p>
                  Demyst's purpose-built external data platform is relied upon by leading
                  organizations.
                </p>
              </div>
              <div className="logo-group">{logos["default"].map(renderCustomerLogo)}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
